import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Users } from "lucide-react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";
import BookingModal from "@/components/BookingModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kindLabel = (kind, lang) => {
  if (kind === "visite") return lang === "fr" ? "Visite" : "Tour";
  if (kind === "festival") return "Festival";
  return "Millenium";
};

const remaining = (e) => Math.max(0, (e.capacity || 0) - (e.booked || 0));

const Events = () => {
  const { lang, t } = useLang();
  const [events, setEvents] = useState([]);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then((res) => setEvents(res.data)).catch(() => setEvents([]));
  }, []);

  const festival = events.find((e) => e.kind === "festival");
  const others = events.filter((e) => e.kind !== "festival");

  return (
    <section id="evenements" data-testid="events-section" className="relative overflow-hidden border-y border-gold/15 bg-ink2">
      <span aria-hidden="true" className="pointer-events-none absolute -right-8 top-10 select-none font-display text-[22vw] leading-none text-transparent opacity-[0.07]" style={{ WebkitTextStroke: "1px rgb(var(--gold))" }}>
        MMXXVII
      </span>

      <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.events.kicker}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 max-w-3xl font-serif text-3xl font-light leading-tight text-parchment sm:text-4xl lg:text-5xl">
            {t.events.title}
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-sand sm:text-lg">{t.events.sub}</p>
        </Reveal>
        <Reveal delay={0.22}>
          <p data-testid="events-inauguration" className="mt-10 inline-flex items-center gap-4 border-l-2 border-gold pl-6 font-serif text-lg font-light italic leading-snug text-parchment/90 sm:text-xl">
            {t.hero.inauguration}
          </p>
        </Reveal>

        {festival && (
          <Reveal delay={0.2}>
            <div data-testid="event-featured" className="gold-frame group relative mt-16 overflow-hidden border border-gold/25">
              <div className="relative aspect-[16/9] overflow-hidden sm:aspect-[21/9]">
                <img
                  src={festival.image}
                  alt={lang === "fr" ? festival.title_fr : festival.title_en}
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
              </div>
              <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 p-6 sm:flex-row sm:items-end sm:justify-between sm:p-10">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {lang === "fr" ? festival.date_label_fr : festival.date_label_en} · {lang === "fr" ? festival.location_fr : festival.location_en}
                  </p>
                  <h3 className="mt-3 max-w-2xl font-serif text-2xl font-light leading-tight text-parchment sm:text-4xl">
                    {lang === "fr" ? festival.title_fr : festival.title_en}
                  </h3>
                </div>
                <button
                  data-testid="event-booking-button"
                  type="button"
                  onClick={() => setBooking(festival)}
                  className="group/btn relative inline-flex shrink-0 items-center gap-2 overflow-hidden rounded-full border border-gold px-7 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:text-ink"
                >
                  <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover/btn:translate-x-0" />
                  <span className="relative flex items-center gap-2">{t.events.book} <ArrowUpRight size={14} /></span>
                </button>
              </div>
            </div>
          </Reveal>
        )}

        <div className="mt-20 space-y-0">
          {others.map((event, i) => (
            <Reveal key={event.id} delay={0.05}>
              <article
                data-testid="event-item"
                className="group grid grid-cols-1 items-center gap-6 border-t border-gold/15 py-10 lg:grid-cols-12 lg:gap-10"
              >
                <div className="lg:col-span-3">
                  <p className="font-serif text-2xl font-light italic leading-snug text-parchment sm:text-3xl">
                    {lang === "fr" ? event.date_label_fr : event.date_label_en}
                  </p>
                  <span className="mt-3 inline-block rounded-full border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">
                    {kindLabel(event.kind, lang)}
                  </span>
                </div>
                <div className="lg:col-span-6">
                  <h3 className="font-serif text-xl font-normal text-parchment transition-colors duration-300 group-hover:text-goldlight sm:text-2xl">
                    {lang === "fr" ? event.title_fr : event.title_en}
                  </h3>
                  <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-sand">
                    <MapPin size={12} className="text-gold" />
                    {lang === "fr" ? event.location_fr : event.location_en}
                    {event.capacity > 0 && (
                      remaining(event) > 0 ? (
                        <span data-testid="event-remaining-seats" className="ml-3 flex items-center gap-1.5 text-sand/70">
                          <Users size={12} /> {remaining(event)} {t.events.remaining}
                        </span>
                      ) : (
                        <span data-testid="event-full-badge" className="ml-3 rounded-full border border-red-400/40 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-red-400">
                          {t.events.full}
                        </span>
                      )
                    )}
                  </p>
                  <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-sand sm:text-base">
                    {lang === "fr" ? event.description_fr : event.description_en}
                  </p>
                </div>
                <div className="flex items-center gap-6 lg:col-span-3 lg:justify-end">
                  {event.image && (
                    <div className="hidden h-20 w-28 shrink-0 overflow-hidden border border-gold/20 lg:block">
                      <img
                        src={event.image}
                        alt={event.title_fr}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                  )}
                  <button
                    data-testid="event-item-booking-button"
                    type="button"
                    onClick={() => setBooking(event)}
                    disabled={event.capacity > 0 && remaining(event) <= 0}
                    aria-label={`${t.events.book} — ${lang === "fr" ? event.title_fr : event.title_en}`}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold transition-all duration-300 hover:bg-gold hover:text-ink disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gold"
                  >
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <BookingModal event={booking} onClose={() => setBooking(null)} />
    </section>
  );
};

export default Events;
